import { useAuiState } from "@assistant-ui/react";
import {
  dateFormats,
  formattedDateTime,
} from "@circle-react/helpers/dateTimeHelpers/formattedDateTime";
import { classNames } from "@circle-react/helpers/twMergeWithCN";
import type { ReactNode } from "react";

import { Typography } from "@/react/components/SettingsApp/CompassCompat/Typography";

import { useIsMessageRunning } from "./messageRunState";
import { useIsLastConversationMessage } from "./threadPosition";

interface MessageActionsProps {
  actionsAlignment: "start" | "end";
  children?: ReactNode;
  className?: string;
  forceVisible?: boolean;
  showTime?: boolean;
}

export const MessageActions = ({
  actionsAlignment,
  children,
  showTime = true,
  className = "",
  forceVisible = false,
}: MessageActionsProps) => {
  const createdAt = useAuiState(s => s.message.createdAt);
  const isRunning = useIsMessageRunning();
  const isLastMessage = useIsLastConversationMessage();

  // A genuinely in-flight run is always the last message in the thread, so we
  // only suppress the actions for that live message. A message that still
  // reports "running" while no longer being the last message is a stale run,
  // e.g. a plan the user walked away from by sending a new message instead of
  // confirming/cancelling.
  if (isRunning && isLastMessage) return null;

  return (
    <div
      className={classNames(
        "flex w-full items-center gap-x-3 transition-opacity",
        !forceVisible &&
          [
            "pointer-events-none opacity-0",
            "peer-hover/bubble:pointer-events-auto peer-hover/bubble:opacity-100",
            "hover:pointer-events-auto hover:opacity-100",
            "focus-within:pointer-events-auto focus-within:opacity-100",
          ].join(" "),
        forceVisible && "pointer-events-auto opacity-100",
        {
          "flex-row-reverse": actionsAlignment === "end",
          [className]: !!className,
        },
      )}
    >
      {children && (
        <div className="flex items-center gap-[5px]">{children}</div>
      )}
      {showTime && (
        <Typography.BodyXs color="text-default">
          {formattedDateTime({
            dateTime: createdAt,
            format: dateFormats.short_time_numeric,
          })}
        </Typography.BodyXs>
      )}
    </div>
  );
};
